
import { Activity, ActivityProduct, BbchStageBase, PesticideIndication, ProductCategoryType } from 'farmdok-rest-api';
import { defineComponent } from 'vue';

import { formatBbchCode, sanitizeBbchCode } from '@/activities/utils/bbchCode';
import {
  filterBbchStages,
  getProductIdsForPesticideIndicationFilter,
  getSelectedPesticideIndications,
} from '@/activities/utils/bbchFiltering';
import { loadBbchStages } from '@/activities/utils/bbchStages';
import { ActionPayloads as PesticideIndicationActionPayloads } from '@/pesticideIndications/store/actions';
import { Api } from '@/plugins/farmdokRestApi';
import { Product } from '@/shared/api/rest/models';
import { DropdownItem, DropdownItemsPerCategory } from '@/shared/components/form/formFieldDropdownTypes';
import notNullOrUndefined from '@/shared/modules/notNullOrUndefinedFilter';
import { RootState } from '@/store/types';

import FourthStep from '../components/FourthStep.vue';
import { formatBbchMacroStageHeader, formatBbchStageDescription, getBbchMacroStage } from './bbchStageFormatting';
import {
  AdditionalDetailsCategory,
  AdditionalDetailsPropertyKey,
  additionalDetailsCategoryHeadlineKeys,
  additionalDetailsProperties,
  isAdditionalDetailsPropertyVisible,
} from './moreDetailsProperties';

type CropWithBbchGroup = Product & {
  bbchGroupId?: string | null;
};

type AdditionalProperty = {
  key: AdditionalDetailsPropertyKey;
  label: string;
  value: number | null;
  various: boolean;
};

type AdditionalPropertyGroup = {
  key: AdditionalDetailsCategory;
  headline: string;
  properties: AdditionalProperty[];
};

export default defineComponent({
  name: 'FourthStepContainer',
  components: { FourthStep },
  data() {
    return {
      bbchStages: [] as BbchStageBase[],
      bbchStagesLoading: false,
    };
  },
  created() {
    this.$store.dispatch('products/crops/subscribe');
    this.$store.dispatch('products/subscribe');
    this.$store.dispatch('productCategories/subscribe');
    this.fetchBbchStages();
  },
  computed: {
    activities(): Activity[] {
      return (this.$store.state as RootState).activities.createEditActivity.activities ?? [];
    },
    selectedBbchCode(): number | null {
      const bbchCodes = this.activities.map((activity) => sanitizeBbchCode(activity.bbchCode));
      const uniqueBbchCodes = [...new Set(bbchCodes)];

      if (uniqueBbchCodes.length !== 1) {
        return null;
      }

      return uniqueBbchCodes[0];
    },
    selectedFieldIds(): string[] {
      return this.activities.map((activity) => activity.fieldId).filter(notNullOrUndefined);
    },
    selectedProductIdsForIndications(): string[] {
      return getProductIdsForPesticideIndicationFilter(this.activities);
    },
    mergedActivityProducts(): ActivityProduct[] {
      return this.$store.getters['activities/createEditActivity/mergedActivityProducts'] ?? [];
    },
    currentActivityProductId(): string | null {
      return (this.$store.state as RootState).activities.createEditActivity.currentActivityProductId;
    },
    selectedProductCategoryType(): ProductCategoryType | null {
      const selectedProductId = this.selectedProductId();
      if (!selectedProductId) {
        return null;
      }

      const selectedProduct = this.findProductById(selectedProductId);
      if (!selectedProduct?.categoryId) {
        return null;
      }

      const productCategories = (this.$store.state as RootState).productCategories.data;
      return productCategories[selectedProduct.categoryId]?.type ?? null;
    },
    showInspectionConducted(): boolean {
      const productCategories = (this.$store.state as RootState).productCategories.data;

      return this.mergedActivityProducts.some((activityProduct) => {
        const { productId } = activityProduct;
        if (productId == null) {
          return false;
        }

        const categoryId = this.findProductById(productId)?.categoryId;
        if (categoryId == null) {
          return false;
        }

        return productCategories[categoryId]?.type === ProductCategoryType.Herb;
      });
    },
    additionalPropertyGroups(): AdditionalPropertyGroup[] {
      const groupedProperties = additionalDetailsProperties.reduce(
        (result: Map<AdditionalDetailsCategory, AdditionalProperty[]>, property) => {
          if (!isAdditionalDetailsPropertyVisible(property, this.selectedProductCategoryType)) {
            return result;
          }

          const currentProperties = result.get(property.category) ?? [];
          const { value, various } = this.getUnifiedActivityPropertyValue(property.key);

          result.set(
            property.category,
            currentProperties.concat({
              key: property.key,
              label: this.formatAdditionalPropertyLabel(property.name, property.unit),
              value,
              various,
            }),
          );

          return result;
        },
        new Map<AdditionalDetailsCategory, AdditionalProperty[]>(),
      );

      const orderedCategories: AdditionalDetailsCategory[] = ['product', 'weather'];
      return orderedCategories
        .map((category) => {
          const properties = groupedProperties.get(category) ?? [];
          if (properties.length < 1) {
            return null;
          }

          return {
            key: category,
            headline: (this.$t(additionalDetailsCategoryHeadlineKeys[category]) as string) ?? '',
            properties,
          };
        })
        .filter(notNullOrUndefined);
    },
    inspectionConducted(): boolean {
      if (this.activities.length < 1) {
        return false;
      }

      return this.activities.every((activity) => activity.inspectionConducted === true);
    },
    selectedBbchGroupIds(): string[] {
      const fields = (this.$store.state as RootState).fields.data;
      const groupIds = this.selectedFieldIds
        .map((fieldId) => fields[fieldId]?.cropId)
        .filter(notNullOrUndefined)
        .map((cropId) => this.findCropById(cropId)?.bbchGroupId)
        .filter(notNullOrUndefined);

      return [...new Set(groupIds)];
    },
    indicationFilterLoading(): boolean {
      if (this.selectedProductIdsForIndications.length < 1) {
        return false;
      }

      const indicationsByProduct = (this.$store.state as RootState).pesticideIndications.data;
      return this.selectedProductIdsForIndications.some((productId) => indicationsByProduct[productId] === undefined);
    },
    cropsLoading(): boolean {
      return this.$store.getters['products/crops/loading'];
    },
    dropdownItemsLoading(): boolean {
      return this.bbchStagesLoading || this.cropsLoading || this.indicationFilterLoading;
    },
    selectedPesticideIndications(): PesticideIndication[] {
      const pesticideIndications = (this.$store.state as RootState).pesticideIndications.data;
      return getSelectedPesticideIndications(this.activities, pesticideIndications);
    },
    filteredBbchStages(): BbchStageBase[] {
      if (this.dropdownItemsLoading) {
        return [];
      }

      const { selectedBbchGroupIds, selectedPesticideIndications } = this;
      return filterBbchStages(this.bbchStages, selectedBbchGroupIds, selectedPesticideIndications);
    },
    showDropdown(): boolean {
      if (this.dropdownItemsLoading) {
        return true;
      }
      return this.filteredBbchStages.length > 0;
    },
    showNoValidBbchRangeWarning(): boolean {
      if (this.dropdownItemsLoading || this.selectedPesticideIndications.length < 1) {
        return false;
      }

      return this.filteredBbchStages.length < 1;
    },
    selectedDropdownItem(): DropdownItem {
      if (this.selectedBbchCode === null) {
        return { id: null, name: '' };
      }

      const selectedStage = this.findBbchStageByCode(this.selectedBbchCode);
      if (!selectedStage) {
        const bbchCodeFormatted = formatBbchCode(this.selectedBbchCode);
        return {
          id: bbchCodeFormatted,
          name: `BBCH ${bbchCodeFormatted}`,
        };
      }

      return this.toDropdownItem(selectedStage);
    },
    dropdownItems(): DropdownItemsPerCategory[] {
      const groupedItems = this.filteredBbchStages.reduce(
        (result: Map<number, { items: DropdownItem[]; macroStageName: string }>, stage) => {
          const macroStage = getBbchMacroStage(stage);
          if (macroStage === null) {
            return result;
          }

          const current = result.get(macroStage) ?? { items: [], macroStageName: '' };
          const currentMacroStageName = stage.macroStageName?.trim() ?? '';
          result.set(macroStage, {
            items: current.items.concat(this.toDropdownItem(stage)),
            macroStageName: current.macroStageName || currentMacroStageName,
          });
          return result;
        },
        new Map<number, { items: DropdownItem[]; macroStageName: string }>(),
      );

      return [...groupedItems.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([macroStage, groupedItem]) => ({
          name: formatBbchMacroStageHeader(macroStage, groupedItem.macroStageName),
          items: groupedItem.items,
        }));
    },
    selectedBbchDescription(): string | null {
      if (this.selectedBbchCode === null) {
        return null;
      }

      const selectedStage = this.findBbchStageByCode(this.selectedBbchCode);
      if (!selectedStage) {
        return `BBCH ${formatBbchCode(this.selectedBbchCode)}`;
      }

      const description = formatBbchStageDescription(selectedStage);
      return description || `BBCH ${formatBbchCode(this.selectedBbchCode)}`;
    },
  },
  watch: {
    selectedProductIdsForIndications: {
      handler(productIds: string[]) {
        if (productIds.length < 1) {
          return;
        }
        const payload: PesticideIndicationActionPayloads['loadByIds'] = productIds;
        this.$store.dispatch('pesticideIndications/loadByIds', payload);
      },
      immediate: true,
    },
  },
  methods: {
    async fetchBbchStages(): Promise<void> {
      this.bbchStagesLoading = true;

      try {
        const api = this.$api as Api;
        this.bbchStages = await loadBbchStages(api);
      } catch (error) {
        console.error(error);
        this.bbchStages = [];
      } finally {
        this.bbchStagesLoading = false;
      }
    },
    updateBbchCode(bbchCode: number | null): void {
      this.$store.commit('activities/createEditActivity/setBbchCode', sanitizeBbchCode(bbchCode));
    },
    updateAdditionalProperty(data: { key: AdditionalDetailsPropertyKey; value: number | null }): void {
      this.$store.commit('activities/createEditActivity/setActivityNumericProperty', data);
    },
    updateInspectionConducted(value: boolean): void {
      this.$store.commit('activities/createEditActivity/setActivityBooleanProperty', {
        key: 'inspectionConducted',
        value,
      });
    },
    findBbchStageByCode(bbchCode: number): BbchStageBase | undefined {
      return (
        this.filteredBbchStages.find((stage) => stage.bbchCode === bbchCode) ??
        this.bbchStages.find((stage) => stage.bbchCode === bbchCode)
      );
    },
    selectedProductId(): string | null {
      if (this.currentActivityProductId) {
        const selectedActivityProduct = this.mergedActivityProducts.find(
          (activityProduct) => activityProduct.id === this.currentActivityProductId,
        );
        if (selectedActivityProduct?.productId) {
          return selectedActivityProduct.productId;
        }
      }

      return this.mergedActivityProducts.find((activityProduct) => !!activityProduct.productId)?.productId ?? null;
    },
    findProductById(productId: string): Product | undefined {
      return this.$store.getters['products/findProductById'](productId);
    },
    getUnifiedActivityPropertyValue(propertyKey: AdditionalDetailsPropertyKey): {
      value: number | null;
      various: boolean;
    } {
      const values = this.activities.map((activity) => this.sanitizeNumericValue(activity[propertyKey]));
      if (values.length < 1) {
        return { value: null, various: false };
      }

      const firstValue = values[0];
      const hasSameValue = values.every((value) => value === firstValue);
      if (hasSameValue) {
        return { value: firstValue, various: false };
      }

      return { value: null, various: true };
    },
    sanitizeNumericValue(value: unknown): number | null {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
      }

      return value;
    },
    formatAdditionalPropertyLabel(name: string, unit: string): string {
      return `${this.$t(name)} in ${unit}`;
    },
    toDropdownItem(stage: BbchStageBase): DropdownItem {
      const bbchCode = sanitizeBbchCode(stage.bbchCode);
      if (bbchCode === null) {
        return { id: null, name: '' };
      }

      const description = stage.description?.trim() ?? '';
      const bbchCodeFormatted = formatBbchCode(bbchCode);

      return {
        id: bbchCodeFormatted,
        name: description ? `BBCH ${bbchCodeFormatted} - ${description}` : `BBCH ${bbchCodeFormatted}`,
      };
    },
    findCropById(cropId: string): CropWithBbchGroup | null {
      const crops = (this.$store.state as RootState).products.crops.data as Record<string, CropWithBbchGroup>;
      if (crops[cropId]) {
        return crops[cropId];
      }

      const crop = Object.values(crops).find((candidate) => String(candidate.id) === String(cropId));
      return crop ?? null;
    },
  },
});
